<template>
<main class="container-1">

  <div class="container-header">
    <Breadcrumb :links="routesBread" />
    <!-- <h2>Listado</h2> -->
    <TabNav :links="routesNav" />
    <p class="password-policy-note">
      Las contraseñas caducan tras un periodo de inactividad configurado en el servidor (60 días
      por defecto). No caducan las cuentas con permiso de gestión de usuarios ni las que nunca han
      iniciado sesión. Un usuario con contraseña caducada recupera el acceso mediante una
      contraseña temporal generada por un administrador.
    </p>
  </div>

  <div class="container-body">

    <div class="table-toolbar">
      <div class="toolbar-left">
        <label>
          Mostrar registros:
          <select v-model="pageSize" @change="onPageSizeChange">
            <option :value="10">10</option>
            <option :value="15">15</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </label>
        </div>
      <div class="toolbar-center">
        <label> Filtro Grupos:
        <select id="group" v-model="selectedGroup" @change="onChangeGroup(selectedGroup)">
          <option value="todas">Cualquier grupo</option>
          <option v-for="item in groups" :key="item.id" :value="item.id">{{ item.name }}</option>
        </select>
        </label>
      </div>
      <div class="toolbar-status">
        <label> Filtro Estado:
        <select id="status" v-model="selectedStatus" @change="onChangeStatus">
          <option value="todos">Todos</option>
          <option value="true">Activo</option>
          <option value="false">Inactivo</option>
        </select>
        </label>
      </div>
      <div class="toolbar-right">
        <label>
          Buscar:
          <input
            type="text"
            v-model="searchQuery"
            @keyup="onSearchKeyup"
            placeholder="Buscar usuario..."
          />
        </label>
      </div>
      <div class="toolbar-clear">
        <button
          v-if="hasActiveFilters"
          class="bulk-btn bulk-btn--clear filters-clear-btn"
          type="button"
          @click="clearFilters"
        >
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Barra de acciones masivas -->
    <div v-if="selectedIds.length > 0" class="bulk-bar">
      <span class="bulk-bar__count">
        {{ selectedIds.length }} usuario{{ selectedIds.length !== 1 ? 's' : '' }} seleccionado{{ selectedIds.length !== 1 ? 's' : '' }}
      </span>
      <button
        v-if="totalPages > 1"
        class="bulk-btn bulk-btn--select-all"
        :disabled="bulkLoading"
        @click="selectAllMatching"
      >
        Seleccionar todos los del filtro
      </button>
      <button class="bulk-btn bulk-btn--activate" :disabled="bulkLoading" @click="bulkSetStatus(true)">
        Activar
      </button>
      <button class="bulk-btn bulk-btn--deactivate" :disabled="bulkLoading" @click="bulkSetStatus(false)">
        Desactivar
      </button>
      <button class="bulk-btn bulk-btn--clear" :disabled="bulkLoading" @click="clearSelection">
        Limpiar selección
      </button>
    </div>

    <!-- Simbología: el botón solo aparece si hay alguna fila marcada, y la
         leyenda arranca oculta para no ocupar espacio a quien ya la conoce -->
    <div v-if="hasInactivityFlags" class="inactivity-legend-toggle">
      <button type="button" class="legend-toggle-btn" @click="showLegend = !showLegend">
        <font-awesome-icon :icon="showLegend ? 'eye-slash' : 'eye'" />
        {{ showLegend ? 'Ocultar simbología' : 'Ver simbología' }}
      </button>
    </div>

    <div v-if="hasInactivityFlags && showLegend" class="inactivity-legend">
      <span class="inactivity-legend__item">
        <i class="inactivity-swatch inactivity-swatch--review"></i> Inactivo con datos: decidir si se elimina
      </span>
      <span class="inactivity-legend__item">
        <i class="inactivity-swatch inactivity-swatch--never"></i> Nunca inició sesión
      </span>
      <span class="inactivity-legend__item">
        <i class="inactivity-swatch inactivity-swatch--inactive"></i> Inactivo por falta de actividad
      </span>
    </div>

    <table class="users-table">
      <thead>
        <tr>
          <th class="col-check">
            <input
              type="checkbox"
              :checked="allPageSelected"
              :indeterminate.prop="somePageSelected && !allPageSelected"
              :disabled="users.length === 0"
              @change="togglePageAll"
            />
          </th>
          <th>#</th>
          <th class="sortable" @click="onSort('firstname')">
            Nombre <font-awesome-icon :icon="getSortIcon('firstname')" />
          </th>
          <th class="sortable" @click="onSort('lastname')">
            Apellido <font-awesome-icon :icon="getSortIcon('lastname')" />
          </th>
          <th class="sortable" @click="onSort('username')">
            Correo electrónico <font-awesome-icon :icon="getSortIcon('username')" />
          </th>
          <th class="sortable" @click="onSort('login')">
            Login <font-awesome-icon :icon="getSortIcon('login')" />
          </th>
          <th class="sortable" @click="onSort('group_name')">
            Grupo <font-awesome-icon :icon="getSortIcon('group_name')" />
          </th>
          <th class="sortable" @click="onSort('created_at')">
            Fecha creación <font-awesome-icon :icon="getSortIcon('created_at')" />
          </th>
          <th class="sortable" @click="onSort('last_login')">
            Último login <font-awesome-icon :icon="getSortIcon('last_login')" />
          </th>
          <th class="sortable" @click="onSort('status')">
            Estado <font-awesome-icon :icon="getSortIcon('status')" />
          </th>
          <th>Acción</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in users"
          :key="item.id"
          :class="[inactivityClass(item), { 'row-inactive': item.status === false, 'row-selected': selectedSet.has(item.id) }]"
          :title="inactivityTitle(item)"
        >
          <td class="col-check">
            <input type="checkbox" :checked="selectedSet.has(item.id)" @change="toggleUser(item.id)" />
          </td>
          <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
          <td>{{ item.firstname }}</td>
          <td>{{ item.lastname }}</td>
          <td>{{ item.username }}</td>
          <td>{{ item.login || 'No posee login' }}</td>
          <td>{{ userGroupName(item) }}</td>
          <td>{{ dateFormatter(item.created_at) }}</td>
          <td>{{ dateTimeFormatter(item.last_login) }}</td>
          <td>{{ item.status === false ? 'Inactivo' : 'Activo' }}</td>
          <td class="action-cell">
            <div class="action-cell__buttons">
              <router-link :to="'/usuarios/' + item.id" class="btn-action btn-ver">
                <font-awesome-icon icon="eye" /> Ver
              </router-link>
              <router-link :to="'/usuarios/' + item.id + '/editar-usuario'" class="btn-action btn-edit">
                Editar
              </router-link>
              <button type="button" class="btn-action btn-delete" @click="openDeleteModal(item)">
                Eliminar
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="users.length === 0">
          <td colspan="11">No hay usuarios</td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-show="totalPages !== 0"
      :totalPages="totalPages"
      :currentPage="currentPage"
      :pageSize="pageSize"
      @pagechanged="pageChangedClick"
    />

</div>

  <!-- <SideNav class="container-sidenav" :links="routesNav" /> -->

  <UserDeleteModal
    v-if="userToDelete"
    :userId="userToDelete.id"
    :userName="userToDelete.firstname + ' ' + userToDelete.lastname"
    @close="closeDeleteModal"
    @deleted="onUserDeleted"
  />

</main>
</template>

<style lang="scss" scoped>
.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1em;
  margin-bottom: 1em;
}

.table-toolbar label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

/*
Alinear "Mostrar registros" & Filtro Grupos & "Buscar" segun observacion SENAPRED 06/2026 ("2. Los selectores: "Mostrar" y "Filtro de grupo" no se encuentran alineados y tampoco tienen el mismo tamaño de texto:" y "3. El "Buscador de texto" no se encuentra alineado.")
1. Dedicar un div para cada herramienta
2. Agregar </label> para cada div (eso permite que cada div tenga el mismo "contenido" a nivel de estilo)
*/
.toolbar-left,
.toolbar-center,
.toolbar-status,
.toolbar-right,
.toolbar-clear{
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75em;
}

// Barra de acciones masivas
.bulk-bar {
  display: flex;
  align-items: center;
  gap: 0.6em;
  flex-wrap: wrap;
  background: #e8f2fb;
  border: 1px solid #b3d4ef;
  border-radius: 5px;
  padding: 0.5em 0.85em;
  margin-bottom: 0.75em;
  font-size: 0.875em;

  &__count {
    font-weight: 600;
    color: var(--color-navy);
    margin-right: 0.25em;
  }
}

.bulk-btn {
  padding: 0.3em 0.75em;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 0.85em;
  line-height: 1.4;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--activate {
    background: #27ae60;
    color: #fff;
    border-color: #1e8449;
    &:hover:not(:disabled) { background: #1e8449; }
  }

  &--deactivate {
    background: #c0392b;
    color: #fff;
    border-color: #a93226;
    &:hover:not(:disabled) { background: #a93226; }
  }

  &--select-all {
    background: #fff;
    color: var(--color-navy);
    border-color: #b3d4ef;
    &:hover:not(:disabled) { background: #d6eaf8; }
  }

  &--clear {
    background: transparent;
    color: #666;
    border-color: #ccc;
    &:hover:not(:disabled) { color: #333; border-color: #999; }
  }
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
}

//Observacion SENAPRED 06/2026 ("4. La tabla tiene los textos muy grandes. Quizás podría ser mas pequeño y así mostrar mas registros.")
.users-table th,
.users-table td {
  padding: 0.6em 0.8em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  text-align: left;
  vertical-align: middle;
  font-size: 0.875em;
}

.users-table thead th {
  background: var(--color-navy);
  color: #fff;
  white-space: nowrap;
  font-weight: 600;
}

.users-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.users-table th.sortable:hover {
  background: var(--color-steel);
}

.users-table tbody tr:hover {
  background: rgba(59, 110, 148, 0.06);
}

.users-table tbody tr.row-inactive {
  opacity: 0.55;
}

/* Estados de inactividad. Van declarados ANTES de .row-selected a propósito:
   tienen la misma especificidad, así que si el administrador selecciona una
   fila marcada gana el azul de la selección y no pierde de vista qué eligió. */
.users-table tbody tr.row-review {
  background: #fff3cd;
  &:hover { background: #ffe9a8; }
}

.users-table tbody tr.row-never-logged {
  background: #fbd5d1;
  &:hover { background: #f8c0ba; }
}

.users-table tbody tr.row-inactivity-threshold {
  background: #f1a9a2;
  &:hover { background: #ec958c; }
}

.users-table tbody tr.row-selected {
  background: #dbeeff;
  &:hover { background: #cce4f9; }
}

.inactivity-legend-toggle {
  margin: 0.6em 0 0.2em;
}

.legend-toggle-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.85em;
  color: var(--color-steel, #3b6e94);
  text-decoration: underline;

  &:hover { opacity: 0.75; }
}

.inactivity-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1.2em;
  margin: 0.2em 0 0.6em;
  font-size: 0.85em;
  color: #555;
}

.inactivity-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
}

.inactivity-swatch {
  width: 1em;
  height: 1em;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.inactivity-swatch--review { background: #fff3cd; }
.inactivity-swatch--never { background: #fbd5d1; }
.inactivity-swatch--inactive { background: #f1a9a2; }

.col-check {
  width: 36px;
  text-align: center !important;
  padding-left: 0.4em !important;
  padding-right: 0.4em !important;

  input[type="checkbox"] {
    cursor: pointer;
    width: 15px;
    height: 15px;
  }
}

.users-table td.action-cell {
  white-space: nowrap;
}

.action-cell__buttons {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.4em;
  align-items: center;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  padding: 0.2em 0.55em;
  border-radius: 3px;
  font-size: 0.82em;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.12s;
  // Estas tres normalizan el <button> de Eliminar contra los <a> de Ver y
  // Editar: el botón nativo trae borde propio y no hereda la tipografía, así
  // que sin esto se veía distinto al resto de la fila.
  border: none;
  font-family: inherit;
  cursor: pointer;
  &:hover { opacity: 0.8; text-decoration: none; }
}

.btn-ver    { background: #e8f4fd; color: var(--color-navy); }
.btn-edit   { background: #fef9e7; color: #7d6608; }
.btn-delete { background: #fdf2f2; color: #922b21; }

.users-table td:nth-child(7),
.users-table td:nth-child(8) {
  white-space: nowrap;
}

.password-policy-note {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #6b7280;
}
</style>

<script>
import SideNav from '@/components/SideNav.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import TabNav from '@/components/TabNav.vue'
import Pagination from '@/components/Pagination.vue'
import UserDeleteModal from '@/components/UserDeleteModal.vue'

import { formatter } from '@/components/mixin/formatter.js'
import { pagination } from '@/components/mixin/pagination.js'
import { restUser } from '@/components/mixin/restUser.js'
import { restGroup } from '@/components/mixin/restGroup.js'

// Estado del listado a nivel de módulo: sobrevive a la navegación interna
// (editar/eliminar y volver) pero se reinicia al recargar la página
let listState = null

export default {
  mixins: [formatter, pagination, restUser, restGroup],
  components: { SideNav, Breadcrumb, Pagination, TabNav, UserDeleteModal },
  data() {
    return {
      selectedGroup: 'todas',
      selectedStatus: 'todos',
      searchQuery: '',
      sortKey: '',
      sortOrder: 'asc',
      pageSize: 15,
      debounceTimer: null,
      selectedIds: [],
      bulkLoading: false,
      userToDelete: null,
      inactivityFlags: {},
      showLegend: false,
    }
  },
  computed: {
    selectedSet() {
      return new Set(this.selectedIds)
    },
    allPageSelected() {
      return this.users.length > 0 && this.users.every(u => this.selectedSet.has(u.id))
    },
    somePageSelected() {
      return this.users.some(u => this.selectedSet.has(u.id))
    },
    hasInactivityFlags() {
      return Object.keys(this.inactivityFlags).length > 0
    },
    hasActiveFilters() {
      return this.searchQuery !== '' ||
        this.selectedGroup !== 'todas' ||
        this.selectedStatus !== 'todos' ||
        this.sortKey !== ''
    },
    routesNav() {
      return [
        { name: 'Opciones' },
        { name: 'Crear usuario', path: '/usuarios/nuevo-usuario' },
        { name: 'Grupos', path: '/usuarios/grupos-de-usuarios' },
        {name: 'Notificaciones', path: '/usuarios/notificaciones'}

        //Trasladar boton 'Crear' segun observacion SENAPRED 06/2026
        //"1. Botón "Crear" no tiene aspecto de botón y no sigue la línea de los otros botones existentes. Tiene bores rectos, no tiene espacio interior y no reacciona igual que los otros. Se sugiere mover al menú de botones laterales"
      ]
    },
    routesBread() {
      return [{ name: 'Usuarios' }]
    }
  },
  watch: {
    // Si la página restaurada quedó fuera de rango (p. ej. se eliminó el último
    // usuario de la última página), volver a la página 1
    users(list) {
      if (list.length === 0 && this.currentPage > 1) {
        this.currentPage = 1
        this.fetchUsers()
      }
      // Los tres endpoints que llenan la tabla pasan por acá, así que el
      // estado de inactividad se recalcula igual para todos
      this.getUsersInactivityFlags(list.map(u => u.id))
    }
  },
  methods: {
    // El estado lo resuelve el backend (GET /users/inactivity-flags) para que
    // mantenga un único umbral de inactividad aunque el barrido esté suspendido.
    inactivityClass(item) {
      switch (this.inactivityFlags[item.id]) {
        case 'inactive': return 'row-inactivity-threshold'
        case 'never_logged': return 'row-never-logged'
        case 'review': return 'row-review'
        default: return ''
      }
    },
    // El color por sí solo no comunica nada a quien no vio la leyenda, y menos
    // a un lector de pantalla: el tooltip dice lo mismo en palabras.
    inactivityTitle(item) {
      switch (this.inactivityFlags[item.id]) {
        case 'inactive': return 'Superó el período configurado sin iniciar sesión'
        case 'never_logged': return 'Nunca ha iniciado sesión'
        case 'review': return 'Lleva mucho tiempo sin conectarse, pero agregó datos: no se elimina solo'
        default: return ''
      }
    },
    openDeleteModal(user) {
      this.userToDelete = user
    },
    closeDeleteModal() {
      this.userToDelete = null
    },
    onUserDeleted() {
      // El usuario borrado puede ser el único de su página: se recarga el listado
      // conservando filtros, orden y página, igual que cualquier otra mutación.
      this.selectedIds = this.selectedIds.filter(id => id !== this.userToDelete.id)
      this.userToDelete = null
      this.fetchUsers()
    },
    fetchUsers() {
      // Persistir aquí cubre todas las mutaciones: filtros, orden, página y pageSize pasan por fetchUsers()
      listState = {
        selectedGroup: this.selectedGroup,
        selectedStatus: this.selectedStatus,
        searchQuery: this.searchQuery,
        sortKey: this.sortKey,
        sortOrder: this.sortOrder,
        pageSize: this.pageSize,
        currentPage: this.currentPage
      }
      if (this.selectedGroup === 'todas') {
        this.getUsersPages(this.currentPage, this.searchQuery, this.sortKey, this.sortOrder, this.pageSize, this.selectedStatus)
      } else {
        this.getUsersByGroup(this.currentPage, this.selectedGroup, this.searchQuery, this.sortKey, this.sortOrder, this.pageSize, this.selectedStatus)
      }
    },
    pageChangedClick(page) {
      this.currentPage = page
      this.fetchUsers()
    },
    onChangeGroup(idGroup) {
      this.currentPage = 1
      this.selectedGroup = idGroup
      this.clearSelection()
      this.fetchUsers()
    },
    onChangeStatus() {
      this.currentPage = 1
      this.clearSelection()
      this.fetchUsers()
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.fetchUsers()
    },
    onSearchKeyup() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.clearSelection()
        this.fetchUsers()
      }, 300)
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'asc'
      }
      this.currentPage = 1
      this.fetchUsers()
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return 'sort'
      return this.sortOrder === 'asc' ? 'sort-up' : 'sort-down'
    },
    clearFilters() {
      clearTimeout(this.debounceTimer)
      this.searchQuery = ''
      this.selectedGroup = 'todas'
      this.selectedStatus = 'todos'
      this.sortKey = ''
      this.sortOrder = 'asc'
      this.currentPage = 1
      this.clearSelection()
      this.fetchUsers()
    },
    userGroupName(user) {
      const names = (user.id_groups || [])
        .map((idGroup) => this.groups.find((element) => element.id === idGroup))
        .filter((group) => group)
        .map((group) => group.name)
      return names.length ? names.join(', ') : 'Grupo desconocido'
    },
    // --- Selección ---
    toggleUser(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx === -1) this.selectedIds.push(id)
      else this.selectedIds.splice(idx, 1)
    },
    togglePageAll() {
      if (this.allPageSelected) {
        const pageIds = new Set(this.users.map(u => u.id))
        this.selectedIds = this.selectedIds.filter(id => !pageIds.has(id))
      } else {
        const existing = new Set(this.selectedIds)
        this.users.forEach(u => { if (!existing.has(u.id)) this.selectedIds.push(u.id) })
      }
    },
    clearSelection() {
      this.selectedIds = []
    },
    async selectAllMatching() {
      try {
        this.bulkLoading = true
        const ids = await this.fetchAllUserIdsForBulk(this.selectedGroup, this.searchQuery, this.selectedStatus)
        this.selectedIds = ids
      } catch (e) {
        console.error('[Users] selectAllMatching:', e)
      } finally {
        this.bulkLoading = false
      }
    },
    // --- Acciones masivas ---
    async bulkSetStatus(status) {
      if (this.selectedIds.length === 0 || this.bulkLoading) return
      this.bulkLoading = true
      try {
        await this.putUsersBulkStatus(this.selectedIds, status)
        this.clearSelection()
        this.fetchUsers()
      } catch (e) {
        console.error('[Users] bulkSetStatus:', e)
      } finally {
        this.bulkLoading = false
      }
    },
  },
  created() {
    if (!this.$store.getters.getPermissions.users) {
      this.$router.push('/pagina-no-encontrada')
    }
    if (listState) {
      this.selectedGroup = listState.selectedGroup
      this.selectedStatus = listState.selectedStatus
      this.searchQuery = listState.searchQuery
      this.sortKey = listState.sortKey
      this.sortOrder = listState.sortOrder
      this.pageSize = listState.pageSize
      this.currentPage = listState.currentPage
    }
  },
  mounted() {
    this.getGroupsNoPages()
    this.fetchUsers()
  }
}
</script>
